import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Input } from './Input'
import { Button } from './Button'
import { Alert } from './Alert'
import { Modal } from './Modal'
import { SignatureFieldPlacer, PlacedField, signerColor } from './SignatureFieldPlacer'
import { useEsignService, SignatureRequestSummary, SignatureRequestSigner } from '../services/esignService'
import { useDocumentService, MatterDocument } from '../services/documentService'
import { SignatureFieldType } from '../services/signService'
import { SignatureIcon } from './icons'
import { ShareLinkPanel } from './ShareLinkPanel'

interface SignatureRequestsProps {
  matterId: string
  documents: MatterDocument[]
  // Prefills the first signer row when opening "New Signature Request" —
  // the matter's own client, since they're the overwhelmingly common first
  // signer on a conveyancing document. Left blank (not defaulted) if the
  // matter has no client or the client has no email on file yet.
  defaultSignerName?: string
  defaultSignerEmail?: string
}

interface SignerDraft {
  name: string
  email: string
}

interface RequestWithSigners {
  signatureRequest: SignatureRequestSummary
  signers: SignatureRequestSigner[]
}

const FIELD_TYPE_OPTIONS: { type: SignatureFieldType; label: string }[] = [
  { type: 'signature', label: 'Signature' },
  { type: 'initial', label: 'Initials' },
  { type: 'date', label: 'Date' },
  { type: 'text', label: 'Text' },
  { type: 'paragraph', label: 'Paragraph' },
]

const formatDate = (value?: string | null) => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const STATUS_LABELS: Record<string, string> = { sent: 'In progress', completed: 'Completed', voided: 'Voided' }
const SIGNER_STATUS_LABELS: Record<string, string> = { pending: 'Waiting', viewed: 'Viewed', signed: 'Signed' }

// Lets a parent (the "Request Signature" button next to each Firm Document
// in MatterDetail.tsx) open this modal pre-selected to a specific document,
// skipping the manual picker below.
export interface SignatureRequestsHandle {
  openForDocument: (documentId: string) => void
}

export const SignatureRequests = forwardRef<SignatureRequestsHandle, SignatureRequestsProps>(({ matterId, documents, defaultSignerName, defaultSignerEmail }, ref) => {
  const { listSignatureRequests, getSignatureRequest, createSignatureRequest, voidSignatureRequest, getSignatureRequestDownloadUrl } = useEsignService()
  const { getDownloadUrl } = useDocumentService()

  const [requests, setRequests] = useState<RequestWithSigners[]>([])
  const [loading, setLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const [showModal, setShowModal] = useState(false)
  const [selectedDocumentId, setSelectedDocumentId] = useState('')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const emptySigner = (): SignerDraft => ({ name: '', email: '' })
  const firstSignerDraft = (): SignerDraft => ({ name: defaultSignerName || '', email: defaultSignerEmail || '' })

  const [signers, setSigners] = useState<SignerDraft[]>([firstSignerDraft()])
  const [activeSignerIndex, setActiveSignerIndex] = useState(0)
  const [activeFieldType, setActiveFieldType] = useState<SignatureFieldType>('signature')
  const [fields, setFields] = useState<PlacedField[]>([])
  const [sending, setSending] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [shareSigner, setShareSigner] = useState<{ link: string; name: string; email: string; fileName: string } | null>(null)

  const tenantDocuments = documents.filter((d) => d.category === 'tenant')

  const loadRequests = async () => {
    setLoading(true)
    setListError(null)
    try {
      const summaries = await listSignatureRequests(matterId)
      const withSigners = await Promise.all(
        summaries.map(async (signatureRequest) => {
          try {
            const detail = await getSignatureRequest(matterId, signatureRequest.id)
            return detail
          } catch {
            return { signatureRequest, signers: [] }
          }
        }),
      )
      setRequests(withSigners)
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load signature requests')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRequests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId])

  const resetModal = () => {
    setSelectedDocumentId('')
    setPreviewUrl(null)
    setSigners([firstSignerDraft()])
    setActiveSignerIndex(0)
    setActiveFieldType('signature')
    setFields([])
    setModalError(null)
  }

  const openModal = () => {
    resetModal()
    setShowModal(true)
  }

  useImperativeHandle(ref, () => ({
    openForDocument: (documentId: string) => {
      resetModal()
      setShowModal(true)
      handleSelectDocument(documentId)
    },
  }))

  const handleSelectDocument = async (documentId: string) => {
    setSelectedDocumentId(documentId)
    setPreviewUrl(null)
    setFields([])
    if (!documentId) return

    setPreviewLoading(true)
    try {
      const url = await getDownloadUrl(matterId, documentId)
      setPreviewUrl(url)
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to load document preview')
    } finally {
      setPreviewLoading(false)
    }
  }

  const updateSigner = (index: number, patch: Partial<SignerDraft>) => {
    setSigners((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)))
  }

  const addSigner = () => setSigners((prev) => [...prev, emptySigner()])

  const removeSigner = (index: number) => {
    setSigners((prev) => prev.filter((_, i) => i !== index))
    setFields((prev) => prev.filter((f) => f.signerIndex !== index).map((f) => (f.signerIndex > index ? { ...f, signerIndex: f.signerIndex - 1 } : f)))
    setActiveSignerIndex((prev) => Math.max(0, Math.min(prev, signers.length - 2)))
  }

  const handleSend = async () => {
    setModalError(null)

    if (!selectedDocumentId) {
      setModalError('Choose a document to send')
      return
    }
    if (signers.some((s) => !s.name.trim() || !s.email.trim())) {
      setModalError('Every signer needs a name and email')
      return
    }
    if (signers.some((_, i) => fields.filter((f) => f.signerIndex === i).length === 0)) {
      setModalError('Every signer needs at least one field placed on the document')
      return
    }

    setSending(true)
    try {
      await createSignatureRequest(
        matterId,
        selectedDocumentId,
        signers.map((s, i) => ({
          name: s.name,
          email: s.email,
          fields: fields
            .filter((f) => f.signerIndex === i)
            .map((f) => ({ type: f.type, page: f.page, xPct: f.xPct, yPct: f.yPct, widthPct: f.widthPct, heightPct: f.heightPct })),
        })),
      )
      setShowModal(false)
      await loadRequests()
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to send for signature')
    } finally {
      setSending(false)
    }
  }

  const handleVoid = async (requestId: string) => {
    try {
      await voidSignatureRequest(matterId, requestId)
      await loadRequests()
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to void signature request')
    }
  }

  const handleDownload = async (requestId: string) => {
    try {
      const url = await getSignatureRequestDownloadUrl(matterId, requestId)
      window.open(url, '_blank')
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to get download link')
    }
  }

  const canPlaceFields = !!previewUrl && signers.length > 0

  return (
    <div className="mt-6 pt-6 border-t border-gray-100">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Signatures</h3>
        <Button type="button" size="sm" onClick={openModal} disabled={tenantDocuments.length === 0} className="inline-flex items-center gap-1.5">
          <SignatureIcon className="w-4 h-4" />
          New Signature Request
        </Button>
      </div>

      {tenantDocuments.length === 0 && (
        <p className="text-xs text-gray-500 mb-3">Upload a Firm Document above before sending it out for signature.</p>
      )}

      {listError && <Alert type="error" className="mb-3">{listError}</Alert>}

      {loading ? (
        <p className="text-gray-600 text-sm">Loading signature requests...</p>
      ) : requests.length === 0 ? (
        <p className="text-gray-500 text-sm">No signature requests yet</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {requests.map(({ signatureRequest, signers: reqSigners }) => {
            // Sequential signing (see backend/src/esign.ts) means at most one
            // signer is ever "up" at a time — the first one not yet signed.
            const currentSigner = reqSigners.find((s) => s.status !== 'signed')
            // Their link already exists too (every signer row is created up
            // front — see esign.ts's POST handler) and can now be previewed
            // early (GET /sign/{token} no longer hard-blocks it), even
            // though they still can't actually submit until it's their turn.
            const nextSigner = currentSigner ? reqSigners.find((s) => s.order === currentSigner.order + 1) : undefined

            return (
              <li key={signatureRequest.id} className="py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{signatureRequest.fileName}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {STATUS_LABELS[signatureRequest.status] || signatureRequest.status} · Sent {formatDate(signatureRequest.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {signatureRequest.status === 'completed' && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => handleDownload(signatureRequest.id)}>
                        Download
                      </Button>
                    )}
                    {signatureRequest.status === 'sent' && currentSigner && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setShareSigner({
                          link: `${window.location.origin}/sign/${currentSigner.id}`,
                          name: currentSigner.name,
                          email: currentSigner.email,
                          fileName: signatureRequest.fileName,
                        })}
                      >
                        {currentSigner.name}&rsquo;s Link
                      </Button>
                    )}
                    {signatureRequest.status === 'sent' && nextSigner && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setShareSigner({
                          link: `${window.location.origin}/sign/${nextSigner.id}`,
                          name: nextSigner.name,
                          email: nextSigner.email,
                          fileName: signatureRequest.fileName,
                        })}
                      >
                        {nextSigner.name}&rsquo;s Link (Next)
                      </Button>
                    )}
                    {signatureRequest.status === 'sent' && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => handleVoid(signatureRequest.id)}>
                        Void
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                  {reqSigners.map((s) => (
                    <span key={s.id} className="text-xs text-gray-600">
                      {s.name}: <span className="font-medium">{SIGNER_STATUS_LABELS[s.status] || s.status}</span>
                    </span>
                  ))}
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title="New Signature Request" size="lg">
        <div className="space-y-6">
          {modalError && <Alert type="error">{modalError}</Alert>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document</label>
            <select
              value={selectedDocumentId}
              onChange={(e) => handleSelectDocument(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">Select a document...</option>
              {tenantDocuments.map((doc) => (
                <option key={doc.id} value={doc.id}>{doc.fileName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Signers</label>
            <div className="space-y-2">
              {signers.map((signer, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: signerColor(i) }} />
                  <Input placeholder="Name" value={signer.name} onChange={(e) => updateSigner(i, { name: e.target.value })} className="flex-1" />
                  <Input placeholder="Email" type="email" value={signer.email} onChange={(e) => updateSigner(i, { email: e.target.value })} className="flex-1" />
                  {signers.length > 1 && (
                    <button type="button" onClick={() => removeSigner(i)} aria-label="Remove signer" className="text-gray-400 hover:text-gray-600 shrink-0">×</button>
                  )}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="ghost" className="mt-2" onClick={addSigner}>
              + Add Signer
            </Button>
          </div>

          {previewLoading && <p className="text-sm text-gray-500">Loading document preview...</p>}

          {canPlaceFields && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Place Fields</label>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <select
                  value={activeSignerIndex}
                  onChange={(e) => setActiveSignerIndex(Number(e.target.value))}
                  className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {signers.map((s, i) => (
                    <option key={i} value={i}>{s.name || `Signer ${i + 1}`}</option>
                  ))}
                </select>
                <div className="flex gap-1">
                  {FIELD_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.type}
                      type="button"
                      onClick={() => setActiveFieldType(opt.type)}
                      className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border ${activeFieldType === opt.type ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <SignatureFieldPlacer
                documentUrl={previewUrl!}
                signerNames={signers.map((s, i) => s.name || `Signer ${i + 1}`)}
                activeSignerIndex={activeSignerIndex}
                fieldType={activeFieldType}
                fields={fields}
                onFieldsChange={setFields}
              />
            </div>
          )}

          <Button type="button" className="w-full" disabled={sending} onClick={handleSend}>
            {sending ? 'Sending...' : 'Send for Signature'}
          </Button>
        </div>
      </Modal>

      <Modal open={!!shareSigner} onClose={() => setShareSigner(null)} title={shareSigner ? `${shareSigner.name}'s Signing Link` : 'Signing Link'}>
        {shareSigner && (
          <ShareLinkPanel
            link={shareSigner.link}
            defaultSubject={`Please sign: ${shareSigner.fileName}`}
            defaultMessage={`Hi ${shareSigner.name},\n\nYou've been asked to sign ${shareSigner.fileName}. Please use the secure link below:\n\n${shareSigner.link}\n\nThis link is unique to you — please don't forward it.`}
            recipientEmail={shareSigner.email}
          />
        )}
      </Modal>
    </div>
  )
})
